import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Stack,
  Grid,
  Divider,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { getClientes } from "../../../services/modules/Clientes";
import { registrarAbono } from "../../../services/modules/Abonos";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoPerfect from "../../../assets/Logo.png";
import { BRANDING } from "../../../config/branding";

// --- helpers ---
const currencyGTQ = new Intl.NumberFormat("es-GT", {
  style: "currency",
  currency: "GTQ",
  minimumFractionDigits: 2
});

const hoyISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 10); // yyyy-MM-dd
};

// PDF
const generarPDFAbono = (cliente, abono, saldoRestante) => {
  const doc = new jsPDF();

  // fecha viene yyyy-MM-dd
  const [año, mes, dia] = String(abono.fecha || "").split("-");
  const fecha = (año && mes && dia) ? `${dia}/${mes}/${año}` : abono.fecha || "";

  const img = new Image();
  img.src = logoPerfect;

  img.onload = () => {
    // Logo
    doc.addImage(img, "png", 10, 10, 60, 38);

    // Empresa
    doc.setFontSize(12);
    let yStart = 50;
    doc.text(BRANDING.pdf.name, 15, yStart);
    // línea en blanco opcional
    // doc.text("", 15, yStart + 6);
    doc.text(`Tel: ${BRANDING.phoneText}`, 15, yStart + 12);
    BRANDING.pdf.addressLines.forEach((line, index) => {
      doc.text(line, 15, yStart + 18 + index * 6);
    });

    // Título
    doc.setFontSize(18);
    doc.text("RECIBO DE ABONO", 105, 30, { align: "center" });

    // Datos cliente/abono
    doc.setFontSize(12);
    doc.text(`Fecha: ${fecha}`, 150, yStart);
    doc.text(`Cliente: ${cliente?.nombre || "—"}`, 150, yStart + 6);
    doc.text(`NIT: ${cliente?.nit || "CF"}`, 150, yStart + 12);

    // Tabla
    autoTable(doc, {
      startY: yStart + 30,
      head: [["Monto Abonado", "Observaciones", "Saldo Restante"]],
      body: [[
        currencyGTQ.format(Number(abono.monto) || 0),
        abono.observaciones || "Sin observaciones",
        currencyGTQ.format(Number(saldoRestante) || 0)
      ]],
      styles: {
        halign: "center",
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
        cellPadding: 3
      },
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: [255, 255, 255]
      },
      theme: "grid"
    });

    doc.save(`Abono_${(cliente?.nombre || "Cliente")}.pdf`);
  };
};

const Abonos = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [clientes, setClientes] = useState([]);
  const [idCliente, setIdCliente] = useState("");
  const [monto, setMonto] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [saldoCliente, setSaldoCliente] = useState(0);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(hoyISO());

  useEffect(() => {
    const cargarClientes = async () => {
      try {
        const data = await getClientes();
        setClientes(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error cargando clientes:", e);
        setClientes([]);
      }
    };
    cargarClientes();
  }, []);

  const handleGuardar = async () => {
    // Validaciones
    if (!idCliente) {
      alert("Debe seleccionar un cliente.");
      return;
    }
    const montoNum = parseFloat(monto);
    if (!Number.isFinite(montoNum) || montoNum <= 0) {
      alert("Ingrese un monto válido (> 0).");
      return;
    }
    if (montoNum > saldoCliente) {
      alert("El monto a abonar no puede ser mayor al saldo actual del cliente.");
      return;
    }
    if (!fechaSeleccionada) {
      alert("Debe seleccionar una fecha para el abono.");
      return;
    }

    const abono = {
      id_cliente: idCliente,
      monto: montoNum,
      observaciones: (observaciones || "").trim(),
      fecha: fechaSeleccionada
    };

    const clienteSeleccionado = clientes.find(c => c.id_cliente === idCliente);

    try {
      const res = await registrarAbono(abono);
      alert(res?.message || "Abono registrado.");

      const saldoRestante = (Number(clienteSeleccionado?.Saldo) || 0) - montoNum;
      generarPDFAbono(clienteSeleccionado, abono, saldoRestante);

      // Limpiar formulario
      setIdCliente("");
      setMonto("");
      setObservaciones("");
      setSaldoCliente(0);
      setFechaSeleccionada(hoyISO());
    } catch (e) {
      console.error("Error al registrar abono:", e);
      alert("Ocurrió un error al registrar el abono.");
    }
  };

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant="h5" gutterBottom>
        Registrar Abono
      </Typography>

      {/* Contenedor principal responsive */}
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha"
              type="date"
              value={fechaSeleccionada}
              onChange={(e) => setFechaSeleccionada(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Cliente"
              value={idCliente}
              onChange={(e) => {
                const id = e.target.value;
                setIdCliente(id);
                const c = clientes.find(x => x.id_cliente === id);
                setSaldoCliente(Number(c?.Saldo) || 0);
              }}
              fullWidth
            >
              {clientes.map(c => (
                <MenuItem key={c.id_cliente} value={c.id_cliente}>
                  {c.nombre}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Resumen de saldo en mobile como tarjeta separada */}
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: isXs ? "grey.50" : "transparent"
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography color="text.secondary">Saldo Actual</Typography>
                <Typography fontWeight={700}>
                  {currencyGTQ.format(Number(saldoCliente) || 0)}
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Monto a Abonar"
              type="number"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Observaciones"
              multiline
              rows={isXs ? 2 : 3}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            color="success"
            onClick={handleGuardar}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Guardar Abono
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Abonos;
